/**
 * The fuzzy scorer every Vowed search surface ranks with: the CLI (`vowed search`), the Vault
 * PWA (vault-pwa/src/search/rank.ts), and the Mac Quick Search panel, which carries a
 * line-for-line Swift port (native/quick-search/Sources/VowedKit/FuzzyMatch.swift).
 *
 * Moved here unchanged from src/cli/commands/search.ts (J-3704) so the TypeScript callers share
 * one implementation instead of two copies. This file must stay import-free: the Vault PWA
 * bundles it, and tests/vault-pwa/bundle-safety.test.ts proves it is the only non-vault input to
 * the phone bundle, so nothing reachable from here can pull CLI or crypto code onto the phone.
 *
 * Simple fuzzy match: checks if all characters in the query appear in order in the target
 * string. Case-insensitive.
 */
export function fuzzyMatch(query: string, target: string): { match: boolean; score: number } {
  const q = query.toLowerCase();
  const t = target.toLowerCase();

  // Exact substring match scores highest
  if (t.includes(q)) {
    return { match: true, score: 100 - t.indexOf(q) };
  }

  // Fuzzy: all query chars appear in order
  let qi = 0;
  let consecutive = 0;
  let maxConsecutive = 0;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      qi++;
      consecutive++;
      maxConsecutive = Math.max(maxConsecutive, consecutive);
    } else {
      consecutive = 0;
    }
  }

  if (qi === q.length) {
    return { match: true, score: maxConsecutive * 10 + (q.length / t.length) * 5 };
  }

  return { match: false, score: 0 };
}
